import React from "react";
import {
  FiArrowLeft,
  FiAward,
  FiBell,
  FiClock,
  FiImage,
  FiMessageSquare,
  FiArrowRight,
  FiArrowUp,
  FiTarget,
  FiType,
} from "react-icons/fi";
import type { DonateWidgetGridBreakpoint } from "services/main/donate.service";
import type {
  AlertIconShape,
  AlertLayoutPreset,
  AlertTheme,
  FlowNodeKind,
  FlowViewportDevice,
  ResizeHandle,
  WidgetConfigState,
  WidgetDefinition,
  WidgetTab,
  WidgetTabOption,
} from "./types";

export const STORAGE_KEY = "donationWidgetCustomizeDraft";
export const DEFAULT_WIDGET_IMAGE =
  "https://sg-1.s3.git-ghost.com/public/images/6c03a7cc-fead-418a-b7cf-ecb3d0e26048.png";
export const ALERT_IMAGE_ACCEPT = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
  "image/heic",
  "image/heif",
].join(",");

export const WIDGET_DEFINITIONS: Record<WidgetTab, WidgetDefinition> = {
  alert: {
    type: "DONATION_ALERT",
    name: "Donation Alert",
    description: "Alert widget for stream donations",
  },
  goal: {
    type: "DONATION_GOAL",
    name: "Donation Goal",
    description: "Goal widget for stream donations",
  },
  timer: {
    type: "CUSTOM",
    name: "Stream Timer",
    description: "Timer widget for stream overlay",
  },
  leaderboard: {
    type: "DONATION_LIST",
    name: "Donation Leaderboard",
    description: "Donation list and leaderboard widget",
  },
  chat: {
    type: "CUSTOM",
    name: "Chat Box",
    description: "Chat box widget for stream overlay",
  },
};

export const ENABLED_WIDGET_TABS: WidgetTab[] = ["alert"];
export const ENABLED_FLOW_NODE_KINDS: FlowNodeKind[] = ["alert"];

export const flowNodePalette: Array<{
  kind: FlowNodeKind;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}> = [
  { kind: "alert", label: "Donation Alert", icon: FiBell, color: "#EF4444" },
  { kind: "goal", label: "Donation Goal", icon: FiTarget, color: "#F59E0B" },
  {
    kind: "leaderboard",
    label: "Donation List",
    icon: FiAward,
    color: "#16A34A",
  },
  { kind: "timer", label: "Timer", icon: FiClock, color: "#168FEB" },
  { kind: "chat", label: "Chat Box", icon: FiMessageSquare, color: "#A855F7" },
  { kind: "text", label: "Text", icon: FiType, color: "#475467" },
  { kind: "image", label: "Image", icon: FiImage, color: "#0EA5E9" },
  { kind: "amount", label: "Amount", icon: FiAward, color: "#059669" },
];

export const enabledFlowNodePalette = flowNodePalette.filter((item) =>
  ENABLED_FLOW_NODE_KINDS.includes(item.kind),
);

export const viewportPresets: Array<{
  key: FlowViewportDevice;
  width: number;
  height: number;
  zoom: number;
}> = [
  { key: "lg", width: 1920, height: 1080, zoom: 0.38 },
  { key: "md", width: 1366, height: 768, zoom: 0.48 },
  { key: "sm", width: 1024, height: 1366, zoom: 0.4 },
  { key: "xs", width: 390, height: 844, zoom: 0.72 },
  { key: "xxs", width: 320, height: 568, zoom: 0.82 },
];

export const DEFAULT_VIEWPORT = viewportPresets[0];

export const RGL_COLS: Record<DonateWidgetGridBreakpoint, number> = {
  lg: 24,
  md: 20,
  sm: 12,
  xs: 8,
  xxs: 4,
};

export const RGL_ROW_HEIGHT = 48;
export const RGL_MARGIN: [number, number] = [8, 8];
export const RGL_CONTAINER_PADDING: [number, number] = [0, 0];
export const FLOW_CANVAS_PADDING = 48;

export const gridSettings: Record<
  FlowViewportDevice,
  {
    cols: number;
    rowHeight: number;
    margin: number;
  }
> = {
  lg: { cols: RGL_COLS.lg, rowHeight: RGL_ROW_HEIGHT, margin: RGL_MARGIN[0] },
  md: { cols: RGL_COLS.md, rowHeight: RGL_ROW_HEIGHT, margin: RGL_MARGIN[0] },
  sm: { cols: RGL_COLS.sm, rowHeight: RGL_ROW_HEIGHT, margin: RGL_MARGIN[0] },
  xs: { cols: RGL_COLS.xs, rowHeight: RGL_ROW_HEIGHT, margin: RGL_MARGIN[0] },
  xxs: { cols: RGL_COLS.xxs, rowHeight: RGL_ROW_HEIGHT, margin: RGL_MARGIN[0] },
};

export const DEFAULT_CONFIG: WidgetConfigState = {
  alert: {
    theme: "sticker",
    layoutPreset: "icon-top",
    minAmount: "1000",
    duration: "100",
    messageTemplate: "{user} ໂດເນດ {amount}ກີບ",
    imageUrl: DEFAULT_WIDGET_IMAGE,
    iconMode: "image",
    iconUrl: DEFAULT_WIDGET_IMAGE,
    iconEmoji: "💜",
    iconShape: "none",
    iconSize: 84,
    soundName: "Chime",
    volume: 78,
    animation: "pop",
    effect: "none",
    backgroundStyle: "none",
    accentColor: "#A855F7",
    textColor: "#FFFFFF",
    showDonorName: true,
    showAmount: true,
    showMessage: true,
    showIcon: true,
    showBorderGlow: true,
  },
  goal: {
    title: "Stream goal",
    current: "367000",
    target: "500000",
    endsAt: "17/06/2026, 21:00",
    layout: "card",
    accentColor: "#168FEB",
    showPercent: true,
  },
  timer: {
    status: "Live",
    label: "Live Stream",
    durationMinutes: "120",
    breakMinutes: "15",
    layout: "card",
    opacity: 100,
    scale: 100,
    showProgress: true,
    allowMinus: true,
  },
  leaderboard: {
    title: "ຈັດອັນດັບນັກໂດເນດສູງສຸດ",
    period: "today",
    limit: "5",
    showAmounts: true,
  },
  chat: {
    title: "Live chat",
    width: "420",
    height: "620",
    fontSize: "14",
    theme: "light",
    showAvatar: true,
    showBadges: true,
  },
};

export const allTabs: WidgetTabOption[] = [
  { key: "alert", label: "ການແຈ້ງເຕືອນ", icon: FiBell },
  { key: "goal", label: "ແຖບເປົ້າໝາຍ", icon: FiTarget },
  { key: "timer", label: "ຈັບເວລາ", icon: FiClock },
  { key: "leaderboard", label: "ຈັດອັນດັບ", icon: FiAward },
  { key: "chat", label: "ກ່ອງແຊັດ", icon: FiMessageSquare },
];

export const tabs = allTabs.filter((tab) => ENABLED_WIDGET_TABS.includes(tab.key));

export const resizeHandles: Array<{
  handle: ResizeHandle;
  label: string;
  className: string;
}> = [
  {
    handle: "right",
    label: "Resize width",
    className:
      "-right-2 top-1/2 h-8 w-3 -translate-y-1/2 cursor-ew-resize rounded-full",
  },
  {
    handle: "bottom",
    label: "Resize height",
    className:
      "bottom-[-8px] left-1/2 h-3 w-8 -translate-x-1/2 cursor-ns-resize rounded-full",
  },
  {
    handle: "corner",
    label: "Resize object",
    className:
      "bottom-[-9px] right-[-9px] h-4 w-4 cursor-nwse-resize rounded-md",
  },
];

export const alertIconShapes: Array<{ value: AlertIconShape; label: string }> = [
  { value: "none", label: "ບໍ່ໃສ່ shape" },
  { value: "circle", label: "ວົງມົນ" },
  { value: "rounded", label: "ມຸມໂຄ້ງ" },
  { value: "square", label: "ສີ່ຫຼ່ຽມ" },
];

export const alertLayoutPresets: Array<{
  key: AlertLayoutPreset;
  title: string;
  description: string;
  theme: AlertTheme;
  icon: React.ComponentType<{ className?: string }>;
  showIcon: boolean;
}> = [
  {
    key: "icon-top",
    title: "Icon top",
    description: "Icon above text, centered",
    theme: "sticker",
    icon: FiArrowUp,
    showIcon: true,
  },
  {
    key: "center",
    title: "Center focus",
    description: "Large centered alert",
    theme: "sticker",
    icon: FiTarget,
    showIcon: true,
  },
  {
    key: "icon-left",
    title: "Icon left",
    description: "Horizontal layout",
    theme: "minimal",
    icon: FiArrowLeft,
    showIcon: true,
  },
  {
    key: "icon-right",
    title: "Icon right",
    description: "Text first, icon on right",
    theme: "banner",
    icon: FiArrowRight,
    showIcon: true,
  },
  {
    key: "banner",
    title: "Wide banner",
    description: "Wide centered overlay",
    theme: "banner",
    icon: FiImage,
    showIcon: true,
  },
  {
    key: "compact",
    title: "Compact",
    description: "Small pill layout",
    theme: "compact",
    icon: FiBell,
    showIcon: true,
  },
  {
    key: "text-only",
    title: "Text only",
    description: "Centered message",
    theme: "marquee",
    icon: FiType,
    showIcon: false,
  },
];

export const isAlertLayoutPreset = (value: unknown): value is AlertLayoutPreset =>
  alertLayoutPresets.some((preset) => preset.key === value);

export const getAlertLayoutPresetFromTheme = (theme: unknown): AlertLayoutPreset => {
  if (theme === "minimal") return "icon-left";
  if (theme === "marquee") return "text-only";
  if (theme === "banner") return "banner";
  if (theme === "compact") return "compact";
  return "icon-top";
};

export const normalizeAlertLayoutPreset = (
  value: unknown,
  fallbackTheme?: unknown,
): AlertLayoutPreset =>
  isAlertLayoutPreset(value)
    ? value
    : getAlertLayoutPresetFromTheme(fallbackTheme);

export const getAlertLayoutPresetDefinition = (
  value: unknown,
  fallbackTheme?: unknown,
) => {
  const presetKey = normalizeAlertLayoutPreset(value, fallbackTheme);
  return (
    alertLayoutPresets.find((preset) => preset.key === presetKey) ||
    alertLayoutPresets[0]
  );
};

export const leaderboardRows = [
  { name: "nova_plays86", amount: 3895.59, tone: "bg-cyan-100 text-cyan-700" },
  { name: "nova", amount: 3824.04, tone: "bg-sky-100 text-sky-700" },
  { name: "rex_mp3", amount: 3781.04, tone: "bg-lime-100 text-lime-700" },
];

export const chatRows = [
  { user: "Bibilie", message: "ສູ້ໆເດີ ເກມນີ້ຊະນະແນ່", color: "#168FEB" },
  { user: "nova", message: "Nice play!", color: "#22C55E" },
  { user: "rex_mp3", message: "ໂດເນດແລ້ວຂຶ້ນຈໍສວຍຫຼາຍ", color: "#A855F7" },
];
